import { useEffect, useMemo, useRef, useState } from 'react'
import { useReducedMotion } from 'framer-motion'
import type {
  ProcedureDraftFields,
  ProcedurePresentation,
} from '@/frontend/features/AgentChats/events/agentEvents'

export type ProcedureRevealField = 'title' | 'objective' | 'steps' | null

interface ProcedureRevealState {
  procedure: ProcedurePresentation | null
  revealing: boolean
  activeField: ProcedureRevealField
  activeStepPosition: number | null
}

interface ProcedureRevealFrame {
  procedure: ProcedurePresentation
  activeField: ProcedureRevealField
  activeStepPosition: number | null
}

const MIN_FRAME_MS = 70
const MAX_FRAME_MS = 140
const MAX_SEQUENCE_MS = 1_100

// The Process tool commits one atomic snapshot. This hook never invents or
// mutates domain data: it reveals the exact before/after field diff in short
// visual frames so a fast tool result still reads as an edit on the canvas.
export function useProgressiveProcedureReveal(
  target: ProcedurePresentation | null,
): ProcedureRevealState {
  const reduceMotion = useReducedMotion()
  const targetKey = useMemo(() => procedureSnapshotKey(target), [target])
  const mountedRef = useRef(false)
  const currentRef = useRef<ProcedurePresentation | null>(target)
  const [state, setState] = useState<ProcedureRevealState>({
    procedure: target,
    revealing: false,
    activeField: null,
    activeStepPosition: null,
  })

  useEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true
      currentRef.current = target
      setState({ procedure: target, revealing: false, activeField: null, activeStepPosition: null })
      return
    }

    if (!target?.draft || reduceMotion || document.hidden) {
      currentRef.current = target
      setState({ procedure: target, revealing: false, activeField: null, activeStepPosition: null })
      return
    }

    const frames = buildProcedureRevealFrames(currentRef.current, target)
    if (frames.length <= 1) {
      currentRef.current = target
      setState({ procedure: target, revealing: false, activeField: null, activeStepPosition: null })
      return
    }

    const timers: number[] = []
    const frameMs = Math.max(
      MIN_FRAME_MS,
      Math.min(MAX_FRAME_MS, Math.floor(MAX_SEQUENCE_MS / Math.max(frames.length - 1, 1))),
    )

    const commitFrame = (frame: ProcedureRevealFrame, revealing: boolean) => {
      currentRef.current = frame.procedure
      setState({
        procedure: frame.procedure,
        revealing,
        activeField: revealing ? frame.activeField : null,
        activeStepPosition: revealing ? frame.activeStepPosition : null,
      })
    }

    commitFrame(frames[0], true)
    frames.slice(1).forEach((frame, index) => {
      const finalFrame = index === frames.length - 2
      timers.push(window.setTimeout(
        () => commitFrame(finalFrame ? { ...frame, procedure: target } : frame, !finalFrame),
        frameMs * (index + 1),
      ))
    })

    return () => timers.forEach((timer) => window.clearTimeout(timer))
  }, [reduceMotion, targetKey])

  return state
}

function buildProcedureRevealFrames(
  current: ProcedurePresentation | null,
  target: ProcedurePresentation,
): ProcedureRevealFrame[] {
  if (!target.draft) return [frame(target)]

  const currentFields = current?.draft?.fields || {}
  const targetFields = target.draft.fields
  let workingTitle = current?.title
  let workingFields: ProcedureDraftFields = {
    ...currentFields,
    steps: [...(currentFields.steps || [])],
  }
  const frames: ProcedureRevealFrame[] = []

  // The first saved draft begins from the truthful empty canvas, then reveals
  // only fields that exist in the committed snapshot.
  if (!current?.draft) {
    frames.push(frame(withFields(target, workingFields, workingTitle)))
  }

  if (workingTitle !== target.title || workingFields.name !== targetFields.name) {
    workingTitle = target.title
    workingFields = { ...workingFields, name: targetFields.name }
    frames.push(frame(withFields(target, workingFields, workingTitle), 'title'))
  }

  const { steps: targetSteps = [], ...targetDetails } = targetFields
  const { steps: workingSteps = [] } = workingFields
  if (!sameValue(withoutSteps(workingFields), targetDetails)) {
    workingFields = { ...workingFields, ...targetDetails, steps: workingSteps }
    frames.push(frame(withFields(target, workingFields, workingTitle), 'objective'))
  }

  let steps = [...workingSteps]
  targetSteps.forEach((step, index) => {
    if (sameValue(steps[index], step)) return

    if (index < steps.length) steps[index] = step
    else steps.push(step)
    workingFields = { ...workingFields, steps: [...steps] }
    frames.push(frame(withFields(target, workingFields, workingTitle), 'steps', step.position))
  })

  while (steps.length > targetSteps.length) {
    const removed = steps.pop()
    workingFields = { ...workingFields, steps: [...steps] }
    frames.push(frame(
      withFields(target, workingFields, workingTitle),
      'steps',
      steps.at(-1)?.position || removed?.position || null,
    ))
  }

  if (frames.length === 0 || !sameValue(frames.at(-1)?.procedure, target)) {
    frames.push(frame(target))
  }

  return frames
}

function withFields(
  target: ProcedurePresentation,
  fields: ProcedureDraftFields,
  title?: string,
): ProcedurePresentation {
  if (!target.draft) return target

  return {
    ...target,
    title,
    draft: {
      ...target.draft,
      fields: {
        ...fields,
        steps: [...(fields.steps || [])],
      },
    },
  }
}

function frame(
  procedure: ProcedurePresentation,
  activeField: ProcedureRevealField = null,
  activeStepPosition: number | null = null,
): ProcedureRevealFrame {
  return { procedure, activeField, activeStepPosition }
}

function withoutSteps(fields: ProcedureDraftFields): Omit<ProcedureDraftFields, 'steps'> {
  const { steps: _steps, ...details } = fields
  return details
}

function sameValue(left: unknown, right: unknown): boolean {
  return JSON.stringify(left) === JSON.stringify(right)
}

function procedureSnapshotKey(procedure: ProcedurePresentation | null): string {
  if (!procedure) return 'empty'

  return JSON.stringify({
    title: procedure.title,
    status: procedure.status,
    phase: procedure.progress?.phase,
    revision: procedure.draft?.revision,
    fields: procedure.draft?.fields,
  })
}
